import React from 'react';
import { Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { ConfirmationNumber, Delete, PersonAddAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { AccountData, OrderData } from '../../../../api';
import { getOrderPage } from '../../../../utils/AccountUtils';
import { useLanguage } from '../../../../common/GeneralUtils';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { getDateFormat, getTimeFormat } from '../../../../common/getDateAndTimeFormat';

type Props = {
  order: OrderData;
  onDelete: (order: OrderData) => void;
};

export const RedeemedOrderRow = ({ order, onDelete }: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const dateFormat = getDateFormat(account).longDateFormat;
  const timeFormat = getTimeFormat(account);
  const orderPage = getOrderPage(account, order.order_page_id);
  const pageName = orderPage?.label?.[language] || orderPage?.label?.en || '';
  const dateLabel = order.date ? moment(order.date).format(dateFormat) : '';
  const timeLabel = order.time != null ? moment.unix(order.time).tz('utc').format(timeFormat) : '';
  const dateTimeLabel = [dateLabel, timeLabel].filter(Boolean).join(' ');
  const orderLabel = `#${order.order_number} - ${pageName} - ${dateTimeLabel}`;

  return (
    <Grid container alignItems="center" justifyContent="space-between" py={0.5}>
      <Grid container alignItems="center" flex={1} flexWrap="nowrap" gap={1}>
        <Grid container alignItems="center" flexWrap="nowrap" width="auto">
          <ConfirmationNumber fontSize="small" sx={{ marginInlineEnd: 1, color: 'text.secondary' }} />
          <Typography variant="body2">{orderLabel}</Typography>
        </Grid>
        {order.guest_order_id && (
          <Tooltip title={t('passes.brought_guest_tooltip')} arrow>
            <Grid container alignItems="center" width="auto" gap={0.25} sx={{ color: 'text.secondary' }}>
              <PersonAddAlt fontSize="small" />
              <Typography variant="caption">{t('passes.brought_guest_badge')}</Typography>
            </Grid>
          </Tooltip>
        )}
      </Grid>
      <Tooltip title={t('delete')}>
        <IconButton size="small" onClick={() => onDelete(order)}>
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
    </Grid>
  );
};
